import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { Send, Loader2, Sparkles, Bot } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function CaseStudyMentorTab({ caseStudyId, company, apiPrefix = "/case-studies" }) {
  const [history, setHistory] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get(`${apiPrefix}/${caseStudyId}/mentor/history`).then((r) => setHistory(r.data)).finally(() => setLoading(false));
  }, [caseStudyId, apiPrefix]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [history]);

  const send = async () => {
    const text = message.trim();
    if (!text) return;
    setHistory((h) => [...h, { role: "user", content: text }]);
    setMessage("");
    setSending(true);
    try {
      const { data } = await api.post(`${apiPrefix}/${caseStudyId}/mentor`, { message: text });
      setHistory((h) => [...h, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setHistory((h) => h.filter((m) => m.content !== text || m.role !== "user"));
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Mentor is unavailable right now — please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="glass-panel rounded-2xl p-5 flex flex-col h-[520px]" data-testid="case-study-mentor-tab">
      <div className="flex items-center gap-2 mb-3 pb-3 border-b border-white/5">
        <Bot className="w-4 h-4 text-indigo-400" />
        <p className="text-sm font-semibold text-white">Ask the AI Mentor about the {company} case study</p>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-3 pr-1" data-testid="case-study-mentor-messages">
        {loading && <Loader2 className="w-5 h-5 text-indigo-400 animate-spin mx-auto mt-8" />}
        {!loading && history.length === 0 && (
          <p className="text-xs text-slate-400 text-center mt-8">
            Stuck on identity resolution, field mapping, or a SQL task? Ask the mentor for a hint — it won't give away the answer, just guide your thinking.
          </p>
        )}
        {history.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`case-study-mentor-msg-${i}`}>
            <div className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-primary text-white" : "bg-white/5 text-slate-300"}`}>
              {m.role === "assistant" ? (
                <div className="prose-chat">
                  <ReactMarkdown>{m.content}</ReactMarkdown>
                </div>
              ) : (
                m.content
              )}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-white/5 rounded-xl px-3.5 py-2.5 flex items-center gap-1.5 text-slate-400 text-sm">
              <Sparkles className="w-3.5 h-3.5 animate-pulse" /> Thinking...
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/5">
        <input
          value={message} onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
          placeholder="Ask the mentor a question..." data-testid="case-study-mentor-input"
          className="flex-1 bg-white/[0.03] border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:border-indigo-400/40"
        />
        <button
          onClick={send} disabled={sending || !message.trim()} data-testid="case-study-mentor-send-btn"
          className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary hover:bg-primary/90 disabled:opacity-50 text-white shrink-0"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
